import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { UnitsGridColumn } from 'src/app/shared/components/units-grid-input/units-grid-input.models';
import { FlowCoefficientSchedule } from 'src/app/perical/models/chokes.model';

export const DEFAULT_CHOKE_CV_SCHEDULE_ROW: FlowCoefficientSchedule = {
  percentOpen: 0,
  flowCoefficient: 0
};

export function createChokeCvScheduleColumns(): readonly UnitsGridColumn[] {
  return [
    {
      kind: 'number',
      field: 'percentOpen',
      header: 'Open/Travel',
      unitSymbol: '%',
      min: 0,
      max: 100,
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'flowCoefficient',
      header: 'Cv',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 3
    }
  ];
}

export function createChokeCvScheduleRow(data?: Partial<FlowCoefficientSchedule>): FormGroup {
  return new FormGroup({
    percentOpen: new FormControl<number | null>(data?.percentOpen ?? null, [
      Validators.required,
      Validators.min(0),
      Validators.max(100)
    ]),
    flowCoefficient: new FormControl<number | null>(data?.flowCoefficient ?? null, [
      Validators.required
    ])
  });
}

export function isChokeCvScheduleRowEmpty(row: FormGroup): boolean {
  const percentOpen = row.get('percentOpen')?.value;
  const flowCv = row.get('flowCoefficient')?.value;

  const isPercentEmpty = percentOpen === null || percentOpen === undefined || percentOpen === '' || Number.isNaN(Number(percentOpen));
  const isCvEmpty = flowCv === null || flowCv === undefined || flowCv === '' || Number.isNaN(Number(flowCv));

  return isPercentEmpty && isCvEmpty;
}

export function ensureTrailingInputRow(formArray: FormArray<FormGroup>): void {
  if (formArray.length === 0) {
    formArray.push(createChokeCvScheduleRow());
    return;
  }

  while (
    formArray.length > 1 &&
    isChokeCvScheduleRowEmpty(formArray.at(formArray.length - 1)) &&
    isChokeCvScheduleRowEmpty(formArray.at(formArray.length - 2))
  ) {
    formArray.removeAt(formArray.length - 1);
  }

  const lastRow = formArray.at(formArray.length - 1);
  if (!isChokeCvScheduleRowEmpty(lastRow)) {
    formArray.push(createChokeCvScheduleRow());
  }
}

export function populateChokeCvScheduleFormArray(
  formArray: FormArray<FormGroup>,
  scheduleData?: readonly FlowCoefficientSchedule[]
): void {
  formArray.clear();
  const rows = scheduleData && scheduleData.length > 0 ? scheduleData : [];

  rows.forEach((item) => {
    formArray.push(createChokeCvScheduleRow(item));
  });

  ensureTrailingInputRow(formArray);
}

export function extractChokeCvScheduleRows(formArray: FormArray<FormGroup>): FlowCoefficientSchedule[] {
  const validRows: FlowCoefficientSchedule[] = formArray.controls
    .filter((control) => !isChokeCvScheduleRowEmpty(control as FormGroup))
    .map((control) => ({
      percentOpen: Number(control.get('percentOpen')?.value ?? 0),
      flowCoefficient: Number(control.get('flowCoefficient')?.value ?? 0)
    }));

  const uniqueRows = validRows.filter(
    (row, index, self) =>
      index === self.findIndex((r) => r.percentOpen === row.percentOpen && r.flowCoefficient === row.flowCoefficient)
  );

  return uniqueRows.length === 0 ? [DEFAULT_CHOKE_CV_SCHEDULE_ROW] : uniqueRows;
}
